import { getLowestPrice, getOriginalPrice } from '@modules/price/parsers';

/**
 * @typedef {Object} RequiredPayload
 * @property {import('@common-vue/media-card-large/src/composables/useUpc/types').UseUpc.UpcData} upcData
 * @property {import('@common-vue/media-card-large/src/composables/useUpc/types').UseUpc.UpcId} selectedUpcId
 * @property {import('@modules/price/parsers').TEMPSelectedPrice} selectedPrice - TODO: add type after defining it in the MCL
 *
 * @param {RequiredPayload} params
 * @returns {import("../types").RequiredPayload}
 */
function _mapRequiredPayload({
  upcData,
  selectedUpcId,
  selectedPrice,
}) {
  const { upcs } = upcData;
  // FYI: We need to show badge for any UPC if UPC is not selected
  const selectedUpc = upcs[selectedUpcId] || Object.values(upcs)[0];
  const { priceTypeId } = selectedPrice;
  const lowestPrice = getLowestPrice(selectedPrice);
  const {
    department, classCode, vendorCode, markStyleCode,
  } = selectedUpc || {};

  return {
    price: lowestPrice ?? undefined,
    dept: department?.departmentId ?? undefined,
    clazz: classCode ?? undefined,
    vendor: vendorCode ?? undefined,
    markStyle: markStyleCode || undefined, // should not be an empty string
    priceType: priceTypeId ?? undefined,
  };
}

/**
 * @typedef {Object} FullMapperParams
 * @property {import('@modules/price/parsers').TEMPSelectedPrice} selectedPrice - TODO: add type after defining it in the MCL
 * @property {number} quantity
 * @property {import('@composables/user/types').UserGUID} userGUID
 * @property {import('@composables/user/types').UserId} userId
 * @property {import('@composables/user/types').BagGuid} bagGuid
 * @property {import('@composables/useLoyalty').TierName} tier
 * @property {import('@composables/useLoyalty').EncryptedLoyaltyId} encryptedLoyaltyId
 * @property {import('@composables/useLoyalty').DefaultTenderType} tenderType
 * @property {import("../types").RequiredPayload} requiredPayload
 *
 * @param {FullMapperParams} params
 * @returns {import("../types").Payload}
 */
function _mapFullPayload({
  selectedPrice,
  quantity,
  userGUID,
  userId,
  bagGuid,
  tier,
  encryptedLoyaltyId,
  tenderType,
  requiredPayload,
}) {
  const originalPrice = getOriginalPrice(selectedPrice);
  const {
    price, dept, clazz, vendor, markStyle, priceType,
  } = requiredPayload;

  return {
    LoyaltyPrePurchaseRequestBO: {
      userGUID,
      userId,
      tier,
      bagGuid,
      encryptedLoyaltyId,
      items: [
        {
          price,
          originalPrice: originalPrice || price,
          quantity: quantity || 1,
          seqNum: 1,
          dept,
          clazz,
          priceType,
          priceSold: price,
          vendor,
          markStyle,
        },
      ],
      tenders: [
        {
          tenderType,
          tenderAmt: price,
        },
      ],
      advisory: [
        {
          code: 'ITEM',
          description: 'PPPC',
        },
      ],
    },
  };
}

/**
 * @typedef {Object} PPCMapperParams
 * @property {import('@common-vue/media-card-large/src/composables/useUpc/types').UseUpc.UpcData} upcData
 * @property {import('@common-vue/media-card-large/src/composables/useUpc/types').UseUpc.UpcId} selectedUpcId
 * @property {Object} selectedPrice - TODO: add type after defining it in the MCL
 * @property {number} quantity
 * @property {import('@composables/user/types').UserGUID} userGUID
 * @property {import('@composables/user/types').UserId} userId
 * @property {import('@composables/user/types').BagGuid} bagGuid
 * @property {import('@composables/useLoyalty').TierName} tier
 * @property {import('@composables/useLoyalty').EncryptedLoyaltyId} encryptedLoyaltyId
 * @property {import('@composables/useLoyalty').DefaultTenderType} tenderType
 *
 * @param {PPCMapperParams} params
 * @returns {import("../types").MapperResult}
 */
export const ppcMapper = ({
  upcData,
  selectedUpcId,
  selectedPrice,
  quantity,
  userGUID,
  userId,
  bagGuid,
  tier,
  encryptedLoyaltyId,
  tenderType,
}) => {
  const requiredPayload = _mapRequiredPayload({
    upcData,
    selectedUpcId,
    selectedPrice,
  });
  const fullPayload = _mapFullPayload({
    selectedPrice,
    requiredPayload,
    userGUID,
    userId,
    tier,
    bagGuid,
    encryptedLoyaltyId,
    quantity,
    tenderType,
  });

  return {
    requiredPayload,
    fullPayload,
  };
};

export default ppcMapper;
